import MeaPage from "../../../components/MeaPage";
import { useRef, useState, useEffect } from "react";
import ContractsListComponent from "./components/ContractsListComponent";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import MeaButton from "../../../components/MeaButton";
import { Toast } from "primereact/toast";
import LatestPaymentsModal from "./modals/LatestPaymentsModal";
import tasksAPI from "../../../services/tasks";
import { PERMISSION_TRAINING_CONTRACTS } from "../../../permissions";
import { useAuthContext } from "../../../hooks/useAuthContext";

const ContractsListPage = () => {
  //
  const listRef = useRef();
  const latestPaymentsModalRef = useRef(null);
  const toast = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.registrationId;
  const [isGenerating, setIsGenerating] = useState(false);
  const { hasPermission, auth } = useAuthContext();
  const accessToken = auth?.access_token || "";
  const [jobId, setJobId] = useState(null);

  // Watch task status when jobId is set
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const data = await tasksAPI.GetTask(schoolId, trainingId, jobId, accessToken);
        
        if (data?.data?.status === "COMPLETED") {
          toast.current.clear();
          toast.current.show({ 
            severity: 'success', 
            summary: 'Succès', 
            detail: 'Contrats générés avec succès!' 
          });
          setJobId(null);
          setIsGenerating(false);
          if (listRef.current) {
            listRef.current.refresh();
          }
        } else if (data?.data?.status === "FAILED") {
          toast.current.clear();
          toast.current.show({ 
            severity: 'error', 
            summary: 'Erreur', 
            detail: 'Erreur lors de la génération des contrats!' 
          });
          setJobId(null);
          setIsGenerating(false);
          // Refresh table to show current state even on failure
          if (listRef.current) {
            listRef.current.refresh();
          }
        } else {
          // Task still running, check again in 1 second
          setTimeout(fetchTaskStatus, 1000);
        }
      } catch (error) {
        console.error("Erreur lors de la vérification du statut:", error);
        toast.current.show({ 
          severity: 'error', 
          summary: 'Erreur', 
          detail: 'Erreur lors de la vérification du statut de la tâche' 
        });
        setJobId(null);
        setIsGenerating(false);
        // Refresh table to show current state even on error
        if (listRef.current) {
          listRef.current.refresh();
        }
      }
    };

    if (jobId) {
      fetchTaskStatus();
    }
  }, [jobId, schoolId, trainingId, accessToken]);

  const handleGenerateContracts = async () => {
    try {
      setIsGenerating(true);
      toast.current.show({ 
        severity: 'info', 
        summary: 'Traitement', 
        detail: 'Génération des contrats en cours...' 
      });
      
      const data = await tasksAPI.GenerateContracts(schoolId, trainingId, accessToken);
      setJobId(data?.data?.id);
    } catch (error) {
      console.error("Erreur lors de la génération des contrats:", error);
      toast.current.show({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Erreur lors de la génération des contrats: ' + error.message,
        life: 5000
      });
      setIsGenerating(false);
    }
  };

  const showGenerateContractsConfirmation = () => {
    confirmDialog({
      message: "Êtes-vous sûr de vouloir générer les contrats pour cette formation ?",
      header: "Confirmation de génération",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-success",
      accept: handleGenerateContracts,
      reject: () => {},
    });
  };

  const handleViewStatistics = () => {
    navigate(`/school/${schoolId}/admin/registrations/${trainingId}/payments/stats`);
  };

  const handlePaymentsSettings = () => {
    navigate(`/school/${schoolId}/admin/registrations/${trainingId}/payments-settings`);
  };

  const handlePrintAllContracts = () => {
    navigate(`/school/${schoolId}/admin/registrations/${trainingId}/print-all-contracts${location.search}`);
  };

  const customActions = (
    <div className="flex flex-column md:flex-row gap-2 w-full md:w-auto">
      <MeaButton
        icon="pi pi-chart-bar"
        label="Statistiques"
        className="p-button-info w-full md:w-auto"
        onClick={handleViewStatistics}
      />
      <MeaButton
        icon="pi pi-history"
        label="Derniers paiements"
        className="w-full md:w-auto"
        onClick={() => latestPaymentsModalRef.current?.show()}
      />
      <MeaButton
        icon="pi pi-cog"
        label="Paramètres"
        className="w-full md:w-auto"
        onClick={handlePaymentsSettings}
      />
      <MeaButton
        icon="pi pi-print"
        label="Imprimer"
        className="w-full md:w-auto"
        onClick={handlePrintAllContracts}
      />
      <MeaButton
        icon="pi pi-credit-card"
        label="Générer les contrats"
        className="p-button-success w-full md:w-auto"
        onClick={showGenerateContractsConfirmation}
        loading={isGenerating}
        permission={PERMISSION_TRAINING_CONTRACTS.CREATE}
      />
    </div>
  );

  //
  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <LatestPaymentsModal
        ref={latestPaymentsModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
      />
      <MeaPage
        //breadcrumb={<SlotsBreadCrumb />}
        title="Liste des contrats"
        subtitle="Gérer l'ensemble des contrats d'enseignements des élèves de cette formation"
        customActions={customActions}
      >
        <ContractsListComponent
          ref={listRef}
          showAddButton={true}
          showFilterButton={true}
          showToolbar={true}
          schoolId={schoolId}
          trainingId={trainingId}
          isLoading={isGenerating}
        />
      </MeaPage>
    </>
  );
};
export default ContractsListPage;
